import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Copy, Crosshair, Download } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, EmptyState, ErrorState, LoadingState, describeError, Tabs, TabList, Tab, TabPanel } from '@/components/primitives';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import { apiGet, copyText, downloadFile } from './api';
import { plainText, plural, utcStamp } from './format';

const PLATFORMS = [
  { key: 'splunk', label: 'Splunk SPL' },
  { key: 'elastic', label: 'Elastic KQL' },
  { key: 'qradar', label: 'QRadar AQL' },
];

function CampaignDetail({ campaign, onBack }) {
  const [state, setState] = useState({ data: null, error: null, loading: true });
  const [platform, setPlatform] = useState('splunk');
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      setState({ data: await apiGet(`/dashboard/threat-hunt-campaign/${campaign.id}`), error: null, loading: false });
    } catch (error) {
      setState({ data: null, error, loading: false });
    }
  }, [campaign.id]);
  useEffect(() => { load(); }, [load]);

  const copy = async (text, label) => {
    if (await copyText(text)) toast.success(`${label} query copied`);
    else toast.error("Couldn't copy to the clipboard");
  };

  const download = async (format) => {
    setDownloading(format);
    try {
      await downloadFile(`/dashboard/threat-hunt-campaign/${campaign.id}/iocs`, `campaign-iocs.${format}`, { params: { format } });
      toast.success(`Indicators downloaded as ${format.toUpperCase()}`);
    } catch (err) {
      let detail = describeError(err);
      // Blob error bodies are not parsed by axios; read the message back out.
      try { detail = JSON.parse(await err.response.data.text()).detail || detail; } catch { /* keep generic */ }
      toast.error("Couldn't download the indicators", { description: detail });
    } finally {
      setDownloading(null);
    }
  };

  const { data } = state;
  const stats = data?.ioc_stats || {};
  const statRows = [['Indicators', stats.total_iocs], ['IPs', stats.ips], ['Domains', stats.domains], ['Hashes', stats.hashes], ['URLs', stats.urls]]
    .filter(([, v]) => v != null);

  return (
    <div className="dash-hunt-detail">
      <div className="dash-toolbar">
        <button type="button" className="is-btn is-btn--ghost is-btn--sm" onClick={onBack}>
          <ArrowLeft aria-hidden="true" /> All campaigns
        </button>
        {campaign.status === 'approved' && <Badge tone="success" size="sm">Reviewed</Badge>}
      </div>

      {state.loading && <LoadingState label="campaign" rows={4} />}
      {state.error && <ErrorState title="Couldn't load this campaign" error={state.error} onRetry={load} />}

      {data && (
        <div className="dash-hunt-layout">
          <div className="dash-hunt-main">
            <h2 className="t-heading" style={{ margin: '0 0 8px' }}>{data.campaign?.name || campaign.name}</h2>
            {data.campaign?.description && (
              <div className="dash-hunt-desc-full"><MarkdownRenderer content={data.campaign.description} /></div>
            )}

            {data.queries ? (
              <Tabs value={platform} onValueChange={setPlatform}>
                <TabList label="Query language">
                  {PLATFORMS.map((p) => <Tab key={p.key} value={p.key}>{p.label}</Tab>)}
                </TabList>
                {PLATFORMS.map((p) => (
                  <TabPanel key={p.key} value={p.key}>
                    <div className="dash-query">
                      {data.queries[p.key]?.query ? (
                        <>
                          <pre tabIndex={0}>{data.queries[p.key].query}</pre>
                          <button type="button" className="is-btn is-btn--secondary is-btn--sm" onClick={() => copy(data.queries[p.key].query, p.label)}>
                            <Copy aria-hidden="true" /> Copy
                          </button>
                        </>
                      ) : (
                        <EmptyState variant="compact" title={`No ${p.label} query`} description="This campaign has no indicators this query language can search for." />
                      )}
                    </div>
                  </TabPanel>
                ))}
              </Tabs>
            ) : (
              <EmptyState variant="compact" title="No hunting queries yet" description="Queries are generated once the campaign has reviewed indicators." />
            )}
          </div>

          <aside className="dash-hunt-side" aria-label="Campaign indicators">
            <div className="dash-hunt-side-title">Indicators</div>
            {statRows.length > 0 ? (
              <div className="dash-ioc-grid">
                {statRows.map(([label, value]) => (
                  <div key={label} className="dash-ioc-cell">
                    <span className="dash-ioc-label">{label}</span>
                    <span className="dash-ioc-value">{Number(value).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            ) : <p className="dash-muted">No indicator counts available.</p>}
            <div className="dash-hunt-downloads">
              {['csv', 'txt'].map((fmt) => (
                <button
                  key={fmt}
                  type="button"
                  className="is-btn is-btn--secondary is-btn--sm"
                  disabled={!stats.total_iocs || downloading !== null}
                  onClick={() => download(fmt)}
                  data-testid={`download-iocs-${fmt}`}
                >
                  <Download aria-hidden="true" /> {downloading === fmt ? 'Preparing…' : `Download ${fmt.toUpperCase()}`}
                </button>
              ))}
            </div>
            {data.last_updated && <div className="dash-meta-row" style={{ marginTop: 12 }}><span>Updated {utcStamp(data.last_updated)}</span></div>}
          </aside>
        </div>
      )}
    </div>
  );
}

/**
 * Hunt campaigns published by the platform team, each with ready-to-run
 * queries for the SIEMs the customer uses.
 */
export default function HuntTab({ resource }) {
  const [selected, setSelected] = useState(null);

  if (selected) return <CampaignDetail campaign={selected} onBack={() => setSelected(null)} />;
  if (resource.loading && !resource.data) return <LoadingState label="hunt campaigns" rows={3} />;
  if (resource.error && !resource.data) return <ErrorState title="Couldn't load hunt campaigns" error={resource.error} onRetry={resource.reload} />;

  const campaigns = resource.data?.campaigns || [];
  if (!campaigns.length) {
    return (
      <EmptyState
        icon={Crosshair}
        title="No hunt campaigns published yet"
        description="Campaigns appear here once the IntelliSecure team publishes hunting queries for threats that match your profile."
      />
    );
  }

  return (
    <>
      <p className="dash-panel-intro">
        {plural(campaigns.length, 'campaign')} with queries for Splunk, Elastic and QRadar. Open one to copy a query into your SIEM.
      </p>
      <div className="dash-hunt-grid">
        {campaigns.map((c) => (
          <button key={c.id} type="button" className="dash-hunt-card" onClick={() => setSelected(c)} style={{ textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer' }}>
            <span className="dash-hunt-title">{c.name}</span>
            <span className="dash-hunt-desc dash-clamp-3">{plainText(c.description) || 'No description provided.'}</span>
            <span className="dash-meta-row">
              {c.ioc_count != null && <span>{plural(c.ioc_count, 'indicator')}</span>}
              {c.created_at && <span>{utcStamp(c.created_at)}</span>}
            </span>
          </button>
        ))}
      </div>
    </>
  );
}
